import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link2, Loader2, Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");

const SuggestionRow = ({ s, onLink, busy }) => (
  <div className="p-3 flex items-center gap-3" data-testid={`veh-ctr-suggestion-${s.id}`}>
    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 font-mono">{s.score}%</Badge>
    <div className="flex-1 text-sm">
      <div className="font-medium">{s.title || "—"} <span className="text-slate-500 font-normal">· {s.provider || "—"}</span></div>
      <div className="text-xs text-slate-500">{s.reasons.join(" · ")} · {fmtDate(s.start_date)} → {fmtDate(s.end_date)}
        {s.monthly != null && ` · ${Number(s.monthly).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} € /mois`}</div>
    </div>
    <Button size="sm" variant="outline" disabled={busy} onClick={() => onLink(s)} data-testid={`veh-ctr-link-${s.id}`}>
      <Link2 className="w-3.5 h-3.5 mr-1" /> Lier
    </Button>
  </div>
);

export const VehicleContractLinker = ({ vehicleId, canWrite, onChanged }) => {
  const [state, setState] = useState({ loading: false, suggestions: [] });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const autoLink = async (silent) => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.post(`/fleet/vehicles/${vehicleId}/auto-link-contracts`);
      setState({ loading: false, suggestions: data.suggestions || [] });
      if (data.linked.length) {
        toast.success(`${data.linked.length} contrat(s) relié(s) automatiquement depuis Dépenses avec engagements`);
        onChanged();
      } else if (!silent) {
        toast.info(data.suggestions.length ? "Aucune correspondance certaine — vérifie les suggestions" : "Aucun contrat correspondant trouvé dans Dépenses avec engagements");
      }
    } catch (e) { setState({ loading: false, suggestions: [] }); if (!silent) toast.error(apiError(e)); }
  };

  const ranFor = useRef(null);
  useEffect(() => {
    if (canWrite && ranFor.current !== vehicleId) { ranFor.current = vehicleId; autoLink(true); }
    /* eslint-disable-next-line */
  }, [vehicleId, canWrite]);

  const link = async (s) => {
    setBusy(true);
    try {
      await api.post(`/fleet/vehicles/${vehicleId}/link-engagement/${s.id}`);
      toast.success("Contrat relié au véhicule");
      setState((st) => ({ ...st, suggestions: st.suggestions.filter((x) => x.id !== s.id) }));
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/fleet/vehicles/${vehicleId}/contracts/import-pdf`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      toast.success(`Contrat lu par l'IA et relié : ${data.engagement.nom_contrat || data.engagement.prestataire}`);
      if (data.plate_mismatch) toast.warning(`Attention : le contrat mentionne l'immatriculation ${data.plate_mismatch}`);
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (!canWrite) return null;
  return (
    <Card className="border-blue-200 bg-blue-50/30" data-testid="veh-ctr-linker">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0 gap-2 flex-wrap">
        <CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-600" />Liaison intelligente des contrats</CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={state.loading || busy} onClick={() => autoLink(false)} data-testid="veh-ctr-autolink-btn">
            {state.loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            {state.loading ? "L'IA recherche…" : "Rechercher dans Engagements"}
          </Button>
          <Button size="sm" disabled={busy} onClick={() => fileRef.current?.click()} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="veh-ctr-upload-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
            {busy ? "Lecture IA…" : "Joindre un PDF"}
          </Button>
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden"
            onChange={(e) => upload(e.target.files?.[0])} data-testid="veh-ctr-upload-input" />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {state.suggestions.length > 0 ? (
          <div className="divide-y border-t bg-white">
            <p className="px-3 pt-3 text-xs text-slate-500">Contrats possibles (correspondance partielle) — à confirmer :</p>
            {state.suggestions.map((s) => <SuggestionRow key={s.id} s={s} onLink={link} busy={busy} />)}
          </div>
        ) : (
          <p className="px-4 pb-3 text-xs text-slate-500">
            L'IA relie automatiquement les contrats de « Dépenses avec engagements » qui mentionnent cette immatriculation ou ce VIN. Sinon, joins le PDF : il sera lu, classé dans Engagements et relié à ce véhicule.
          </p>
        )}
      </CardContent>
    </Card>
  );
};
